import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AdminGuard, AdminTitle, EmailList } from "@/components/admin/AdminGuard";
import { OrderStatus, OrderSummary } from "@/components/OrderSummary";
import { errText } from "@/lib/api";
import { adminCancelOrder, adminOrder, adminOrders, adminOrderStatus, adminResendShipped } from "@/lib/admin";
import { cad } from "@/lib/site";

const FILTERS = ["all", "paid", "fulfilled", "shipped", "pending_payment", "expired"];
const NEXT = { paid: ["fulfilled", "Mark fulfilled"], fulfilled: ["shipped", "Mark shipped"] };

const CARRIERS = ["Canada Post", "Purolator", "UPS", "FedEx", "DHL", "Other"];

const ShipForm = ({ o, onDone, onCancel }) => {
  const [f, setF] = useState({ carrier: "Canada Post", tracking_number: "", tracking_url: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const r = await adminOrderStatus(o.id, "shipped", f); onDone(r); toast.success(`Order ${r.order_number} shipped, email queued`); } catch (err) { toast.error(errText(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="mt-6 grid gap-5 border-[1.5px] border-ink bg-pink/30 p-5 sm:grid-cols-3" data-testid="admin-ship-form">
      <label><span className="label text-ink/60">Carrier</span><select value={f.carrier} onChange={(e) => setF({ ...f, carrier: e.target.value })} className="field" data-testid="admin-ship-carrier">{CARRIERS.map((c) => <option key={c}>{c}</option>)}</select></label>
      <label><span className="label text-ink/60">Tracking number</span><input required value={f.tracking_number} onChange={(e) => setF({ ...f, tracking_number: e.target.value })} className="field font-mono" data-testid="admin-ship-number" /></label>
      {f.carrier === "Other" && <label><span className="label text-ink/60">Tracking URL</span><input required type="url" placeholder="https://" value={f.tracking_url} onChange={(e) => setF({ ...f, tracking_url: e.target.value })} className="field" data-testid="admin-ship-url" /></label>}
      <div className="flex items-center gap-5 sm:col-span-3"><button disabled={busy} className="btn btn-ink !h-11" data-testid="admin-ship-submit">{busy ? "Saving…" : "Mark shipped + email customer"}</button><button type="button" onClick={onCancel} className="link-u" data-testid="admin-ship-cancel">Cancel</button></div>
    </form>
  );
};

const Detail = ({ id, onChange }) => {
  const [o, setO] = useState(null);
  useEffect(() => { setO(null); adminOrder(id).then(setO).catch((e) => toast.error(errText(e))); }, [id]);
  const [shipping, setShipping] = useState(false);
  const refresh = () => [4000, 12000].forEach((t) => setTimeout(() => adminOrder(id).then(setO).catch(() => {}), t));
  if (!o) return <p className="label text-ink/60">Loading…</p>;
  const next = NEXT[o.status];
  const resend = async () => {
    try { setO(await adminResendShipped(o.id)); toast.success("Shipping email queued"); refresh(); } catch (e) { toast.error(errText(e)); }
  };
  const advance = async () => {
    if (next[0] === "shipped") return setShipping(true);
    try { const r = await adminOrderStatus(o.id, next[0]); setO(r); onChange(); toast.success(`Order ${r.order_number} is now ${r.status}`); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="border-[1.5px] border-ink p-6" data-testid="admin-order-detail">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div><p className="display text-4xl" data-testid="admin-order-number">{o.order_number}</p><p className="mt-1 text-sm text-ink/70">{o.email}{o.phone && ` · ${o.phone}`}</p></div>
        <div className="flex items-center gap-4"><OrderStatus status={o.status} />{next && <button onClick={advance} className="btn btn-ink !h-11" data-testid="admin-order-advance">{next[1]}</button>}
          {["paid", "fulfilled"].includes(o.status) && <button onClick={async () => { if (!window.confirm("Cancel this order? Crew Points earned on it are reversed.")) return; try { setO(await adminCancelOrder(o.id)); onChange(); toast.success("Order cancelled"); } catch (e) { toast.error(errText(e)); } }} className="link-u text-alert" data-testid="admin-order-cancel">Cancel</button>}</div>
      </div>
      {shipping && <ShipForm o={o} onCancel={() => setShipping(false)} onDone={(r) => { setO(r); setShipping(false); onChange(); refresh(); }} />}
      {o.tracking && <div className="mt-4 flex justify-end"><button onClick={resend} className="link-u" data-testid="admin-resend-shipped">Resend shipping email</button></div>}
      {o.oversold?.length > 0 && <p className="mt-4 bg-pink px-3 py-2 text-sm" data-testid="admin-order-oversold">Oversold: {o.oversold.join(", ")}</p>}
      <div className="mt-6"><OrderSummary order={o} /></div>
      <div className="mt-6 grid gap-6 border-t border-ink/10 pt-5 md:grid-cols-2">
        <div><p className="label text-ink/60">History</p><ul className="mt-2 space-y-1 font-mono text-xs" data-testid="admin-order-history">{o.status_history.map((h, i) => <li key={i}>{h.status} · {new Date(h.at).toLocaleString("en-CA")}</li>)}</ul>
          <p className="label mt-4 text-ink/60">Stripe session</p><p className="break-all font-mono text-[11px]">{o.stripe_session_id}</p></div>
        <div><p className="label text-ink/60">Emails</p><div className="mt-2"><EmailList emails={o.emails} testid="admin-order-emails" /></div></div>
      </div>
    </div>
  );
};

const Panel = () => {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState("all");
  const [rows, setRows] = useState(null);
  const selected = params.get("id");
  const load = () => adminOrders({ status }).then(setRows).catch((e) => toast.error(errText(e)));
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <AdminTitle right={<select value={status} onChange={(e) => setStatus(e.target.value)} className="field !w-auto font-mono text-sm" data-testid="admin-orders-filter">{FILTERS.map((s) => <option key={s}>{s}</option>)}</select>}>Orders</AdminTitle>
      <div className="mt-8 grid gap-8 xl:grid-cols-12">
        <div className="xl:col-span-5">
          <p className="label text-ink/60" data-testid="admin-orders-count">{rows ? `${rows.length} orders` : "Loading…"}</p>
          <ul className="mt-3" data-testid="admin-orders-list">
            {rows?.map((o) => (
              <li key={o.id}>
                <button onClick={() => setParams({ id: o.id })} className={`flex w-full items-center gap-3 border-b border-ink/10 px-2 py-3 text-left text-sm transition-colors hover:bg-ink/5 ${selected === o.id ? "bg-sky/30" : ""}`} data-testid={`admin-order-row-${o.order_number}`}>
                  <span className="font-mono text-xs">{o.order_number}</span><span className="flex-1 truncate">{o.name}</span><OrderStatus status={o.status} /><span className="w-20 text-right">{cad(o.total)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="xl:col-span-7">{selected ? <Detail id={selected} onChange={load} /> : <p className="text-sm text-ink/60">Select an order to see details.</p>}</div>
      </div>
    </>
  );
};

export default function AdminOrders() {
  return <AdminGuard><Panel /></AdminGuard>;
}
